/**
 * /ndt-inspection/welds — the weld register across jobs (GET
 * /ndt-inspection/welds, fieldiq-api#1144). Full width; a row opens the weld
 * on its own page. Filters (standing chip, job, welder, search) live in the URL.
 *
 * A weld's STANDING is the API's: the worst latest exam across its methods,
 * or Unexamined. Its QUALIFICATION badge is the API's warning — never a block.
 *
 * CHIP COUNTS are a tally of the list filtered by job / welder / search but
 * not by standing — the API returns no per-standing counts. The endpoint caps
 * at 2,000 rows; the footer says so when a list reaches it.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ClipboardList, Search } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useNdtJobs, useNdtWelders, useNdtWelds } from '../../hooks/useNdtInspection';
import { queryLoadFailure } from '../../lib/load-failure';
import { formatNdtValue, ndtWeldStandingSkin } from '../../lib/ndt-inspection';
import { NDT_WELD_STANDINGS, type NdtWeld } from '../../types/ndt-inspection';
import { useOpenRecord } from '../downhole/recordPage';
import { NDT_INPUT, NdtBadge, NdtChip, NdtChipRow, NdtEmpty, NdtPanel, NdtRegister, ndtDay, type NdtColumn } from './NdtBits';
import { NdtQualBadge } from './NdtJobBits';

const API_ROW_CAP = 2000;

export default function WeldsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [params, setParams] = useSearchParams();
  const standing = params.get('standing') ?? '';
  const jobId = params.get('job') ?? '';
  const welderId = params.get('welder') ?? '';
  const q = params.get('q') ?? '';
  const [search, setSearch] = useState(q);
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };
  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== q) setParam('q', search.trim());
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const base = { jobId: jobId || undefined, welderId: welderId || undefined, q: q || undefined };
  const listQuery = useNdtWelds({ ...base, standing: standing || undefined });
  const baseQuery = useNdtWelds(base);
  const jobs = useNdtJobs();
  const welders = useNdtWelders();
  const welds = listQuery.data?.data ?? [];
  const units = listQuery.data?.meta?.units ?? null;
  const all = baseQuery.isLoading || queryLoadFailure(baseQuery) ? undefined : baseQuery.data?.data;
  const countOf = (pick: (w: NdtWeld) => boolean) => (all ? all.filter(pick).length : undefined);

  const columns: NdtColumn<NdtWeld>[] = [
    { key: 'number', header: 'Weld', render: (w) => <span className="font-mono font-extrabold text-white">{w.weldNumber || '—'}</span> },
    { key: 'job', header: 'Job', render: (w) => <span className="font-mono">{w.jobNumber || '—'}</span> },
    { key: 'line', header: 'Line / spool', render: (w) => [w.lineRef, w.spool].filter(Boolean).join(' · ') || '—' },
    { key: 'size', header: units?.length ? `Dia × wall (${units.length})` : 'Dia × wall', render: (w) => `${formatNdtValue(w.diameter, null)} × ${formatNdtValue(w.wall, null)}` },
    { key: 'welder', header: 'Welder', render: (w) => (w.welderName ? `${w.welderName}${w.welderStamp ? ` (${w.welderStamp})` : ''}` : '—') },
    { key: 'date', header: 'Welded', render: (w) => ndtDay(w.weldDate) },
    { key: 'qual', header: 'Qualification', render: (w) => <NdtQualBadge status={w.welderQualification?.status} compact /> },
    { key: 'standing', header: 'Standing', render: (w) => <NdtBadge skin={ndtWeldStandingSkin(w.standing)} value={w.standing} /> },
  ];
  const filtered = Boolean(standing || jobId || welderId || q);

  return (
    <div data-testid="ndt-welds-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT inspection"
        title="Welds"
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <ClipboardList className="size-4" aria-hidden="true" />, label: 'Welds', sub: filtered ? 'In this filter' : 'On record', tone: 'neutral', loading: baseQuery.isLoading, value: displayValue(countOf(() => true)) },
          { icon: <AlertTriangle className="size-4" aria-hidden="true" />, label: 'Repair', sub: 'Awaiting repair', tone: 'accent', loading: baseQuery.isLoading, value: displayValue(countOf((w) => w.standing === 'repair')) },
          { icon: <CheckCircle2 className="size-4" aria-hidden="true" />, label: 'Accepted', tone: 'success', loading: baseQuery.isLoading, value: displayValue(countOf((w) => w.standing === 'accept')) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <NdtChipRow label="Filter by standing">
            <NdtChip label="All" active={!standing} count={countOf(() => true)} onClick={() => setParam('standing', '')} />
            {NDT_WELD_STANDINGS.map((s) => (
              <NdtChip
                key={s}
                label={ndtWeldStandingSkin(s).label}
                active={standing === s}
                count={countOf((w) => w.standing === s)}
                onClick={() => setParam('standing', s)}
              />
            ))}
          </NdtChipRow>
          <div className="grid gap-3 md:grid-cols-[1fr_240px_240px]">
            <label className="relative block">
              <span className="sr-only">Search weld, line, spool, station or job</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search weld, line, spool, station or job"
                className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </label>
            <select aria-label="Job" className={NDT_INPUT} value={jobId} onChange={(e) => setParam('job', e.target.value)}>
              <option value="">All jobs</option>
              {(jobs.data?.data ?? []).map((j) => (
                <option key={j.id} value={j.id}>
                  {j.jobNumber} · {j.customerName ?? '—'}
                </option>
              ))}
            </select>
            <select aria-label="Welder" className={NDT_INPUT} value={welderId} onChange={(e) => setParam('welder', e.target.value)}>
              <option value="">All welders</option>
              {(welders.data?.data ?? []).map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                  {w.welderStamp ? ` (${w.welderStamp})` : ''}
                </option>
              ))}
            </select>
          </div>

          <NdtPanel
            eyebrow="Weld register"
            title="Welds"
            testId="ndt-weld-register"
            footer={
              !listQuery.isLoading && !queryLoadFailure(listQuery) && welds.length > 0
                ? welds.length >= API_ROW_CAP
                  ? `Showing the first ${API_ROW_CAP} welds — narrow the filters to see the rest`
                  : `${welds.length} weld${welds.length === 1 ? '' : 's'}`
                : undefined
            }
          >
            <NdtRegister
              query={listQuery}
              rows={welds}
              columns={columns}
              rail={(w) => ndtWeldStandingSkin(w.standing).rail}
              onOpen={(w) => openRecord(`/ndt-inspection/welds/${w.id}`)}
              subject="welds"
              testId="ndt-weld"
              minWidth="min-w-[1080px]"
              empty={filtered ? <NdtEmpty>No weld matches these filters.</NdtEmpty> : <NdtEmpty>No welds are recorded yet. Welds are added on a job.</NdtEmpty>}
              card={(w) => (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-mono text-sm font-extrabold text-white">
                      {w.weldNumber} <span className="text-fieldiq-muted">· {w.jobNumber || '—'}</span>
                    </p>
                    <p className="mt-0.5 text-xs text-fieldiq-muted">{w.welderName || 'No welder'} · {ndtDay(w.weldDate)}</p>
                  </div>
                  <NdtBadge skin={ndtWeldStandingSkin(w.standing)} value={w.standing} />
                </div>
              )}
            />
          </NdtPanel>
        </div>
      </section>
    </div>
  );
}
